import React, { useEffect, useState, useCallback, useMemo } from "react";
import useAccess from "../../hooks/useAccess";
import axios from "axios";
import StyledSelect from "../../components/StyledSelect";
import SecurityRoleManager from "./SecurityRoleManager";
import AdminPageHeader from "../../components/AdminPageHeader";

import "../../styles/PermissionManager.css";
import "../../styles/AdministrationPages.css";

function PermissionManager() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [resources, setResources] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedResource, setSelectedResource] = useState("");
  const [localChanges, setLocalChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("permissions");

  const { hasPermission, permissionV2Enforced } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    permissions: {
      edit: hasPermission("PermissionManagerPage", "Permissions", "edit"),
    },
  };

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // -------------------- Load Data --------------------
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [roleRes, pageRes, permRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/emp-role`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/pages`, { headers }),
        axios.get(`${BASE_URL}/api/permissions`, { headers }),
      ]);
      setRoles(roleRes.data);
      setPages(pageRes.data);
      setPermissions(permRes.data);
    } catch (err) {
      console.error("❌ Failed to load initial data:", err);
      alert("Failed to load permissions. Check API connection.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, headers]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load all resources when page changes
  useEffect(() => {
    if (!selectedPage) {
      setResources([]);
      return;
    }
    const fetchResources = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/dropdowns/resources?page_id=${selectedPage}`,
          { headers },
        );
        setResources(res.data);
      } catch (err) {
        console.error("❌ Failed to load resources:", err);
      }
    };
    fetchResources();
  }, [selectedPage, BASE_URL, headers]);

  // -------------------- Helpers --------------------

  const STANDARD_ACTIONS = ["view", "edit", "delete"];

  // Find the current page being edited
  const currentPage = pages.find((p) => p.p_id === Number(selectedPage));
  const selectedRoleIsSuperAdmin =
    roles.find((role) => role.r_id === Number(selectedRole))?.r_name ===
    "Super Admin";

  // Helper to get allowed actions per resource dynamically
  const getActionsForResource = () => STANDARD_ACTIONS;

  const checkRolePermission = (roleId, pageId, resId, action) => {
    // Check if there's a local override first
    const local = localChanges.find(
      (c) =>
        c.role_id === roleId &&
        c.page_id === pageId &&
        c.res_id === resId &&
        c.action === action,
    );
    if (local) return local.enabled;

    // Otherwise check if it exists in saved permissions
    return permissions.some(
      (p) =>
        p.role_id === roleId &&
        p.page_id === pageId &&
        p.res_id === resId &&
        p.action === action,
    );
  };

  const toggleAction = (roleId, pageId, resId, action) => {
    if (!editing || saving) return;
    setLocalChanges((prev) => {
      const existingIndex = prev.findIndex(
        (c) =>
          c.role_id === roleId &&
          c.page_id === pageId &&
          c.res_id === resId &&
          c.action === action,
      );

      if (existingIndex !== -1) {
        // Flip local state
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          enabled: !updated[existingIndex].enabled,
        };
        return updated;
      }

      // If not in local changes, base toggle on the current *computed* state
      const currentEnabled = checkRolePermission(roleId, pageId, resId, action);

      return [
        ...prev,
        {
          role_id: roleId,
          page_id: pageId,
          res_id: resId,
          action,
          enabled: !currentEnabled, // toggle it
        },
      ];
    });
  };

  const saveAll = async () => {
    if (!selectedRole || !selectedPage) {
      alert("Please select Role and Page before saving.");
      return;
    }

    try {
      setSaving(true);
      const changes = [...localChanges];
      for (const c of changes) {
        if (c.enabled) {
          await axios.post(`${BASE_URL}/api/permissions`, c, { headers });
        } else {
          const existing = permissions.find(
            (p) =>
              p.role_id === c.role_id &&
              p.page_id === c.page_id &&
              p.res_id === c.res_id &&
              p.action === c.action,
          );
          if (existing) {
            await axios.delete(
              `${BASE_URL}/api/permissions/${existing.perm_id}`,
              { headers },
            );
          }
        }
      }

      const refreshedPermissions = await axios.get(
        `${BASE_URL}/api/permissions`,
        { headers },
      );
      setPermissions(refreshedPermissions.data);
      setLocalChanges([]);
      setEditing(false);

      // ✅ 🔄 Notify all components (like AdminDashboard) to refresh
      window.dispatchEvent(new Event("permissionsUpdated"));

      alert("✅ Permissions saved successfully!");
    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setLocalChanges([]);
  };

  // -------------------- UI --------------------

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3">Loading...</p>
      </div>
    );
  }

  // Filter resources if user picked one specifically
  const visibleResources =
    selectedResource && selectedResource !== ""
      ? resources.filter((r) => Number(r.res_id) === Number(selectedResource))
      : resources;

  return (
    <div className="admin-standard-page permission-manager">
      <AdminPageHeader
        icon="fas fa-shield-alt"
        title="Security Centre"
        description="Configure role permissions and maintain the security-role catalogue."
      />

      <section className="admin-standard-workspace">

        <div className="security-centre-tabs admin-standard-tabs" role="tablist">
          <button
            type="button"
            data-no-standard-button="true"
            className={
              activeTab === "permissions" ? "security-tab-selected active" : ""
            }
            onClick={() => setActiveTab("permissions")}
          >
            Permissions
          </button>
          <button
            type="button"
            data-no-standard-button="true"
            className={activeTab === "roles" ? "security-tab-selected active" : ""}
            onClick={() => setActiveTab("roles")}
          >
            Roles
          </button>
        </div>

        <div className="admin-standard-workspace-body">
        {!permissionV2Enforced && (
          <div className="permission-rollout-notice" role="status">
            Permission V2 is in safe rollout mode. Admin roles currently receive
            View and Edit access; Delete remains Super Admin-only. The new
            database catalogue has not been activated.
          </div>
        )}

        {activeTab === "roles" && <SecurityRoleManager />}
        {activeTab === "permissions" && (
          <>
            {/* Filters */}
            <div className="card p-3 mb-4 nw-filter-panel admin-standard-filter-panel">
              <div className="row align-items-center g-3 nw-filter-grid">
                <div className="col-md-3">
                  <label className="form-label fw-bold">Role</label>
                  <StyledSelect
                    value={selectedRole}
                    onChange={setSelectedRole}
                    options={[
                      { value: "", label: "Select Role" },
                      ...roles.map((role) => ({
                        value: role.r_id,
                        label: role.r_name,
                      })),
                    ]}
                    minWidth={0}
                    ariaLabel="Filter permissions by role"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Page</label>
                  <StyledSelect
                    value={selectedPage}
                    onChange={(value) => {
                      setSelectedPage(value);
                      setSelectedResource(""); // reset resource on page change
                    }}
                    options={[
                      { value: "", label: "Select Page" },
                      ...pages.map((page) => ({
                        value: page.p_id,
                        label: page.p_name === "AccountSetting" ? "Admin Settings" : page.p_name,
                      })),
                    ]}
                    minWidth={0}
                    ariaLabel="Filter permissions by page"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Resource</label>
                  <StyledSelect
                    value={selectedResource}
                    onChange={setSelectedResource}
                    disabled={!resources.length}
                    options={[
                      { value: "", label: "All Resources" },
                      ...resources.map((resource) => ({
                        value: resource.res_id,
                        label: resource.res_name,
                      })),
                    ]}
                    minWidth={0}
                    ariaLabel="Filter permissions by resource"
                  />
                </div>
              </div>
            </div>

            {/* Permissions Table */}
            {selectedRole && selectedPage && (
              <div className="card p-3 permission-results-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold text-primary mb-0">
                    {pages.find((p) => p.p_id === Number(selectedPage))?.p_name === "AccountSetting"
                      ? "Admin Settings"
                      : pages.find((p) => p.p_id === Number(selectedPage))?.p_name}
                  </h6>

                  {PERMISSIONS.permissions.edit &&
                    (!editing ? (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setEditing(true)}
                      >
                        Edit
                      </button>
                    ) : (
                      <div>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={saveAll}
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    ))}
                </div>

                {visibleResources.map((res) => {
                  const activeActions = getActionsForResource(
                    currentPage?.p_code || currentPage?.p_name,
                    res.res_name,
                  ).filter((a) =>
                    checkRolePermission(
                      Number(selectedRole),
                      Number(selectedPage),
                      res.res_id,
                      a,
                    ),
                  );

                  return (
                    <div key={res.res_id} className="permission-row mb-3">
                      <div className="fw-semibold">{res.res_name}</div>
                      {!editing ? (
                        <div className="text-muted">
                          {activeActions.length > 0
                            ? activeActions.join(", ")
                            : "No access"}
                        </div>
                      ) : (
                        <div className="d-flex flex-wrap gap-3 mt-2">
                          {getActionsForResource(
                            currentPage?.p_code || currentPage?.p_name,
                            res.res_name,
                          ).map((a) => (
                            <div key={a} className="form-check form-switch">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={checkRolePermission(
                                  Number(selectedRole),
                                  Number(selectedPage),
                                  res.res_id,
                                  a,
                                )}
                                onChange={() =>
                                  toggleAction(
                                    Number(selectedRole),
                                    Number(selectedPage),
                                    res.res_id,
                                    a,
                                  )
                                }
                                disabled={
                                  !editing ||
                                  saving ||
                                  (a === "delete" && !selectedRoleIsSuperAdmin)
                                }
                                aria-disabled={
                                  a === "delete" && !selectedRoleIsSuperAdmin
                                }
                                title={
                                  a === "delete" && !selectedRoleIsSuperAdmin
                                    ? "Delete permission is available only to Super Admin."
                                    : undefined
                                }
                              />
                              <label className="form-check-label text-capitalize">
                                {a}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        </div>
      </section>
    </div>
  );
}

export default PermissionManager;
